import React, { useState } from "react";
import axios from "axios";
import { Calculator, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIPriceEstimator() {
  const [leiras, setLeiras] = useState("");
  const [suros, setSuros] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const examples = [
    "3 db konnektor cserélése a nappaliban",
    "60m² lakás teljes villany hálózat újrahúzása",
    "Biztosítéktábla bővítés FI relével",
    "Okos lámpakapcsoló (Shelly) beüzemelés 4 helyiségbe",
  ];

  const submit = async () => {
    if (!leiras.trim()) {
      setError("Kérjük írja le röviden a munkát.");
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/ai/price-estimate`, { leiras, suros });
      setResult(data);
    } catch (e) {
      setError("Hiba történt a kalkuláció során. Próbálja újra.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat("hu-HU").format(n);

  return (
    <div data-testid="ai-price-estimator" className="border-2 border-[#262626] bg-black p-5 md:p-7">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-[#FFD700] flex items-center justify-center">
          <Calculator className="w-5 h-5 text-black" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-heading uppercase text-lg leading-none">AI Árkalkulátor</div>
          <div className="font-mono text-xs text-[#A3A3A3] mt-1">Becsült ár HUF-ban, magyar piaci adatok alapján</div>
        </div>
      </div>

      <label className="font-mono text-xs uppercase tracking-widest text-[#A3A3A3] block mb-2">
        Munka leírása
      </label>
      <textarea
        data-testid="price-input"
        className="input-brutal min-h-[120px] mb-3"
        placeholder="pl. 2 szobás lakás, fürdőszoba felújítás, új konnektorok, lámpa, FI relé csere..."
        value={leiras}
        onChange={(e) => setLeiras(e.target.value)}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {examples.map((ex, i) => (
          <button
            key={i}
            onClick={() => setLeiras(ex)}
            data-testid={`price-example-${i}`}
            className="font-mono text-[11px] uppercase tracking-wider px-2 py-1 border border-[#262626] text-[#A3A3A3] hover:border-[#FFD700] hover:text-[#FFD700] transition-colors"
          >
            + {ex}
          </button>
        ))}
      </div>

      <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={suros}
          onChange={(e) => setSuros(e.target.checked)}
          data-testid="price-emergency-toggle"
          className="w-5 h-5 accent-[#FFD700]"
        />
        <span className="font-mono text-sm text-[#F5F5F5]">
          0-24 vészhelyzeti kiszállás (<span className="text-[#FFD700]">+50% felár</span>)
        </span>
      </label>

      {error && <div data-testid="price-error" className="font-mono text-sm text-[#FF3333] mb-3">{error}</div>}

      <button
        data-testid="price-submit"
        onClick={submit}
        disabled={loading}
        className="btn-primary w-full justify-center disabled:opacity-60"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Kalkulálok...</> : <><Sparkles className="w-4 h-4" /> Becsült ár</>}
      </button>

      {result && (
        <div data-testid="price-result" className="mt-6 border-2 border-[#FFD700] p-5 bg-[#FFD700]/5">
          <div className="font-mono text-xs uppercase tracking-widest text-[#FFD700] mb-2">
            [ Eredmény · AI becslés ]
          </div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-heading text-4xl md:text-5xl text-white">
              {fmt(result.ar_tol_huf)}–{fmt(result.ar_ig_huf)}
            </span>
            <span className="font-mono text-sm text-[#FFD700]">Ft</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 font-mono text-sm">
            <div className="border border-[#262626] p-3">
              <div className="text-[10px] uppercase text-[#A3A3A3] tracking-widest">Időtartam</div>
              <div className="text-white">{result.idotartam}</div>
            </div>
            <div className="border border-[#262626] p-3">
              <div className="text-[10px] uppercase text-[#A3A3A3] tracking-widest">Komplexitás</div>
              <div className="text-white capitalize">{result.komplexitas}</div>
            </div>
          </div>
          <div className="mt-4 font-mono text-sm text-[#A3A3A3] leading-relaxed">
            <span className="text-[#FFD700]">⚡ Indoklás:</span> {result.magyarazat}
          </div>
          {result.javaslat && (
            <div className="mt-3 font-mono text-sm text-[#A3A3A3]">
              <span className="text-[#FFD700]">→ Javaslat:</span> {result.javaslat}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-[#262626]">
            <a href="#kapcsolat" data-testid="price-to-contact" className="btn-primary w-full justify-center">
              Pontos ajánlat kérése
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
